import React, { useState, useEffect } from "react";
import {
	AppBar,
	Toolbar,
	Typography,
	Box,
	Slide,
	Fade,
	Button,
	Link,
	Avatar,
	Container,
	TextField,
	FormControlLabel,
	Checkbox,
	useMediaQuery,
	useTheme,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import "./App.css";

import whiteLogo from "./SwiftMed Horizontal WHITE.png";
import colorLogo from "./SwiftMed Horizontal.png";
import logoOnly from "./SwiftMed Logo.png";
// import genericBackground from "./generic-background.png";
import deskBackground from "./desk-background.png";
import mainScreenshot from "./main-screenshot.png";
import secondScreenshot from "./second-screenshot.png";

import medicareLogo from "./medicare_logo_transparent.png";

const brandColor = "#78c4dc";
const alternateColor = "#d0e6f2";

function App() {
	const [showLanding, setShowLanding] = useState(true);
	const [showMainContent, setShowMainContent] = useState(false);
	const [showNavbar, setShowNavbar] = useState(false);
	const [currentPage, setCurrentPage] = useState("main");

	useEffect(() => {
		setTimeout(() => {
			setShowLanding(false); // Start hiding landing page
			setShowMainContent(true); // Show main content
			setTimeout(() => setShowNavbar(true), 1000); // Show navbar after transition
		}, 3000); // Landing stays for 3 seconds
	}, []);

	const scrollToSection = (id: string) => {
		if (currentPage === "main") {
			const section = document.getElementById(id);
			if (section) {
				section.scrollIntoView({ behavior: "smooth" });
			}
		} else {
			setCurrentPage("main");
			setTimeout(() => {
				const section = document.getElementById(id);
				if (section) {
					section.scrollIntoView({ behavior: "smooth" });
				}
			}, 300); // Delay to allow re-rendering before scrolling
		}
	};

	const navigateTo = (page: React.SetStateAction<string>) => {
		window.scrollTo({ top: 0, behavior: "smooth" });
		setCurrentPage(page);
	};

	const [mobileOpen, setMobileOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Show hamburger on medium screens and below

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	// Close drawer when an item is selected
	const handleMenuItemClick = (action: { (): void; (): void }) => {
		setMobileOpen(false);
		action(); // Execute the action function
	};

	const navItems = [
		{ label: "About Us", action: () => scrollToSection("about") },
		{ label: "Services", action: () => scrollToSection("services") },
		{ label: "Contact", action: () => scrollToSection("contact") },
		{ label: "FAQs", action: () => navigateTo("faq") },
		{ label: "Privacy Policy", action: () => navigateTo("privacy-policy") },
		{ label: "Sign Up", action: () => navigateTo("sign-up") },
	];

	return (
		<Box sx={{ minHeight: "100vh", overflow: "hidden", bgcolor: "white" }}>
			<Slide in={showNavbar} direction="down">
				<AppBar
					elevation={0}
					position="fixed"
					sx={{ bgcolor: brandColor }}
				>
					<Toolbar>
						<img
							src={whiteLogo}
							alt="SwiftMed Logo"
							style={{
								height: "42px",
								animation: "fadeUp 2s ease-in forwards",
							}}
							onClick={() => scrollToSection("landing-section")}
						/>
						{!isMobile ? (
							<Box
								sx={{
									display: "flex",
									gap: 3,
									flexGrow: 1,
									justifyContent: "flex-end",
								}}
							>
								{navItems.map((item) => (
									<Button
										key={item.label}
										color="inherit"
										onClick={item.action}
									>
										{item.label}
									</Button>
								))}
								<Button
									disableElevation
									variant="contained"
									href="https://app.swiftmed.com.au/"
									sx={{ bgcolor: "#ff7043", marginLeft: 2 }}
								>
									Log in
								</Button>
							</Box>
						) : (
							/* Show Hamburger Menu on Mobile */
							<Box
								sx={{
									flexGrow: 1,
									display: "flex",
									justifyContent: "flex-end",
								}}
							>
								<IconButton
									edge="end"
									color="inherit"
									onClick={handleDrawerToggle}
								>
									<MenuIcon />
								</IconButton>
							</Box>
						)}
					</Toolbar>
					<Drawer
						anchor="right"
						open={mobileOpen}
						onClose={handleDrawerToggle}
						sx={{
							"& .MuiDrawer-paper": {
								width: 250,
								color: "white",
								backgroundColor: brandColor,
							},
						}}
					>
						<List sx={{ textAlign: "right", paddingRight: 2 }}>
							{navItems.map((item) => (
								<ListItem key={item.label} disablePadding>
									<ListItemButton
										onClick={() =>
											handleMenuItemClick(item.action)
										}
									>
										<ListItemText
											primary={item.label}
											sx={{ textAlign: "right" }}
										/>
									</ListItemButton>
								</ListItem>
							))}
							<ListItem disablePadding>
								<ListItemButton
									component="a"
									href="https://app.swiftmed.com.au/"
								>
									<ListItemText
										primary="Log in"
										sx={{ textAlign: "right" }}
									/>
								</ListItemButton>
							</ListItem>
						</List>
						<Box
							sx={{
								position: "absolute",
								bottom: 20,
								right: 20, // Aligns logo to the right
								textAlign: "right",
							}}
						>
							<img
								src={logoOnly}
								alt="SwiftMed Logo"
								style={{ maxWidth: "80px" }}
							/>
						</Box>
					</Drawer>
				</AppBar>
			</Slide>
			{showLanding && (
				<Fade in={showLanding} timeout={1000}>
					<Box
						id="landing"
						sx={{
							height: "100vh",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							textAlign: "center",
							bgcolor: brandColor,
						}}
					>
						<img
							src={whiteLogo}
							alt="SwiftMed Logo"
							style={{
								width: "40%",
								maxWidth: "300px",
								animation: "fadeUp 2s ease-in forwards",
							}}
						/>
					</Box>
				</Fade>
			)}
			{showMainContent && (
				<Slide in={showMainContent} direction="up" timeout={1000}>
					<Box>
						{currentPage === "main" && (
							<>
								<LandingSection navigateTo={navigateTo} />
								<About />
								<Services isMobile={isMobile} />
								<Contact
									navigateTo={navigateTo}
									scrollToSection={scrollToSection}
								/>
							</>
						)}
						{currentPage === "privacy-policy" && <PrivacyPolicy />}
						{currentPage === "faq" && <FAQ />}
						{currentPage === "terms-and-conditions" && (
							<TermsOfService />
						)}
						{currentPage === "sign-up" && (
							<SignUp navigateTo={navigateTo} />
						)}
						<Footer
							navigateTo={navigateTo}
							scrollToSection={scrollToSection}
						/>
					</Box>
				</Slide>
			)}
		</Box>
	);
}

export default App;

export const Footer = ({
	navigateTo,
	scrollToSection,
}: {
	navigateTo: (page: string) => void;
	scrollToSection: (id: string) => void;
}) => (
	<Box
		component="footer"
		sx={{
			bgcolor: brandColor,
			color: "white",
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			padding: "16px 32px",
			mt: 4,
		}}
	>
		<Box>
			<Typography variant="body2" sx={{ mb: 1 }}>
				<Link
					href="#"
					color="inherit"
					underline="none"
					onClick={() => navigateTo("terms-and-conditions")}
				>
					Terms and Conditions
				</Link>
			</Typography>
			<Typography variant="body2" sx={{ mb: 1 }}>
				<Link
					href="#"
					color="inherit"
					underline="none"
					onClick={() => navigateTo("privacy-policy")}
				>
					Privacy Policy
				</Link>
			</Typography>
			<Typography variant="body2">
				<Link
					href="#"
					color="inherit"
					underline="none"
					onClick={() => scrollToSection("contact")}
				>
					Contact
				</Link>
			</Typography>
		</Box>
		<Box>
			<img
				src={whiteLogo}
				alt="SwiftMed Logo"
				style={{ height: "42px" }}
			/>
		</Box>
	</Box>
);

export const LandingSection = ({
	navigateTo,
}: {
	navigateTo: (page: string) => void;
}) => (
	<Box
		id="landing-section"
		sx={{
			height: "100vh",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			alignItems: "center",
			textAlign: "center",
			bgcolor: brandColor,
			padding: 4,
		}}
	>
		<Typography
			variant="h2"
			sx={{ color: "white", fontWeight: "bold", mb: 2 }}
		>
			Welcome to SwiftMed
		</Typography>
		<Typography
			variant="h5"
			sx={{ color: "white", maxWidth: "600px", mb: 4 }}
		>
			Transforming healthcare administration with efficiency and
			simplicity.
		</Typography>
		<Button
			disableElevation
			variant="contained"
			sx={{
				bgcolor: "#ff7043",
				fontSize: "1.2rem",
				padding: "12px 24px",
			}}
			onClick={() => navigateTo("sign-up")}
		>
			Register
		</Button>
	</Box>
);

export const About = () => (
	<Box
		id="about"
		sx={{
			minHeight: "100vh",
			display: "flex",
			alignItems: "start",
			textAlign: "center",
			bgcolor: "white",
			flexDirection: "column",
		}}
	>
		<Box
			sx={{
				flex: 2,
				background: "white",
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				justifyContent: "start",
				alignItems: "center",
				textAlign: "left",
				paddingTop: "100px",
			}}
		>
			<Box sx={{ maxWidth: "800px", paddingX: 4 }}>
				<img
					src={colorLogo}
					alt="SwiftMed Logo"
					style={{
						height: "60px",
						animation: "fadeUp 2s ease-in forwards",
					}}
				/>
				<Typography variant="h5" sx={{ mt: 2 }}>
					Mission
				</Typography>
				<Typography sx={{ mt: 1 }}>
					SwiftMed is dedicated to streamlining healthcare
					administration. Our mission is to revolutionize
					administrative efficiency by eliminating redundant tasks,
					empowering healthcare professionals to streamline the
					management of patient lists and billing.
				</Typography>
				<Typography variant="h5" sx={{ mt: 3 }}>
					Why We Formed
				</Typography>
				<Typography sx={{ mt: 1 }}>
					SwiftMed was founded to address a pervasive issue in the
					healthcare industry—the inefficiency of manual data entry.
					Recognizing that essential data was already available but
					underutilized, we set out to create a seamless solution that
					simplifies data transfer and enhances productivity for
					healthcare professionals.
				</Typography>
				<Typography variant="h5" sx={{ mt: 3 }}>
					What We Do
				</Typography>
				<Typography sx={{ mt: 1 }}>
					At SwiftMed, we streamline the transformation of data from
					sources like operating lists into actionable, user-friendly
					formats. Our innovative platform enables effortless data
					export, sharing, and integration, ensuring that healthcare
					professionals have the right information at their
					fingertips, precisely when they need it.
				</Typography>
				<img
					src={medicareLogo}
					alt="Medicare Logo"
					style={{
						width: "40%",
						maxWidth: "300px",
						animation: "fadeUp 2s ease-in forwards",
					}}
				/>
			</Box>
		</Box>
		<Box
			sx={{
				display: "flex",
				width: "100%",
				justifyContent: "space-evenly",
				gap: 10, // Increased space between profiles
				marginTop: 6,
				paddingBottom: 6,

				flexDirection: { xs: "column", md: "row" }, // Stack on small screens, row on larger
				alignItems: "center",
			}}
		>
			{/* Profile 1 */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<Avatar
					src="/path-to-profile1.jpg" // Update with actual image path
					alt="G"
					sx={{ width: 250, height: 250, mb: 2 }} // Increased avatar size
				/>
				<Typography variant="h6" fontWeight="bold">
					Dr Galaxy Wong
				</Typography>
				<Typography variant="body2" color="gray">
					Co-Founder
				</Typography>
				<Typography variant="body2" color="gray">
					Surgical Assistant
				</Typography>
			</Box>

			{/* Profile 2 */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<Avatar
					src="/path-to-profile2.jpg" // Update with actual image path
					alt="J"
					sx={{ width: 250, height: 250, mb: 2 }} // Increased avatar size
				/>
				<Typography variant="h6" fontWeight="bold">
					Mr Jon Macrohon
				</Typography>
				<Typography variant="body2" color="gray">
					Co-Founder
				</Typography>
				<Typography variant="body2" color="gray">
					Registered Nurse
				</Typography>
			</Box>

			{/* Profile 3 */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<Avatar
					src="/path-to-profile3.jpg" // Update with actual image path
					alt="M"
					sx={{ width: 250, height: 250, mb: 2 }} // Increased avatar size
				/>
				<Typography variant="h6" fontWeight="bold">
					Ms Mizu Ardra
				</Typography>
				<Typography variant="body2" color="gray">
					Co-Founder
				</Typography>
				<Typography variant="body2" color="gray">
					Partner
				</Typography>
			</Box>
		</Box>
	</Box>
);
export const Services = ({ isMobile }: { isMobile: any }) => (
	<Box
		id="services"
		sx={{
			height: "100vh",
			display: "flex",
			flexDirection: "column",
			position: "relative",
		}}
	>
		<Box
			sx={{
				flex: 0.33,
				backgroundImage: `url(${deskBackground})`,
				backgroundAttachment: "fixed",
				backgroundSize: "cover",
				backgroundPosition: "center",
				position: "relative",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "4rem",
			}}
		>
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					bgcolor: "rgba(0, 0, 0, 0.5)",
				}}
			/>
		</Box>
		<Box
			sx={{
				position: "absolute",
				top: isMobile ? "25%" : "30%",
				left: "50%",
				transform: "translate(-50%, -50%)",
				display: "flex",
				gap: "1rem",
				alignItems: "center",
			}}
		>
			<img
				src={mainScreenshot}
				alt="App Screenshot 1"
				style={{
					width: isMobile ? "80vw" : "40vw",
					maxWidth: "1000px",
					height: "auto",
					borderRadius: "8px",
					boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
				}}
			/>
			<img
				src={secondScreenshot}
				alt="App Screenshot 2"
				style={{
					width: "20vw",
					maxWidth: "600px",
					height: "auto",
					borderRadius: "8px",
					boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
					display: isMobile ? "none" : "block",
				}}
			/>
		</Box>
		<Box
			sx={{
				flex: 1,
				bgcolor: alternateColor,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				padding: "2rem",
			}}
		>
			<Box sx={{ maxWidth: "800px" }}>
				<Typography
					variant="h4"
					textAlign="center"
					sx={{ marginBottom: "1rem" }}
				>
					Our Services
				</Typography>
				<Typography variant="h6" textAlign="center">
					SwiftMed is a powerful web-based platform designed to
					simplify and optimise healthcare administration. Our
					intuitive solution streamlines scheduling, patient
					management, and documentation, allowing medical
					professionals to focus on providing quality care. With
					secure cloud-based access, seamless integrations, and
					automated workflows, SwiftMed enhances efficiency and
					reduces administrative burdens for healthcare providers.
				</Typography>
			</Box>
		</Box>
	</Box>
);
export const Contact = ({
	navigateTo,
	scrollToSection,
}: {
	navigateTo: (page: string) => void;
	scrollToSection: (id: string) => void;
}) => (
	<Box
		id="contact"
		sx={{
			height: "100vh",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			textAlign: "center",
			bgcolor: "white",
			padding: 4,
		}}
	>
		<Box sx={{ marginTop: "100px" }}>
			<Typography variant="h4" gutterBottom>
				Contact Us
			</Typography>
			<Box sx={{ textAlign: "left", maxWidth: "600px" }}>
				<Typography variant="h6" gutterBottom>
					Privacy Policy
				</Typography>
				<Typography>
					Please refer to our privacy policy{" "}
					<Link href="#" onClick={() => navigateTo("privacy-policy")}>
						here
					</Link>
					.
				</Typography>
				<Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
					Contact us
				</Typography>
				<Typography>Phone:</Typography>
				<Typography>Email: info@swiftmed.com.au</Typography>
				<Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
					Terms of Service
				</Typography>
				<Typography>
					[Ensure we have enough scope to be able to receive and
					process PDFs]
				</Typography>
			</Box>
		</Box>
	</Box>
);

export const FAQ = () => (
	<Box
		id="faq"
		sx={{
			padding: 4,
			minHeight: "100vh",
			textAlign: "center",
			paddingTop: "100px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		}}
	>
		<Box sx={{ maxWidth: "600px" }}>
			<Typography variant="h4" gutterBottom>
				Frequently Asked Questions
			</Typography>
			<Box sx={{ textAlign: "left" }}>
				<Typography>
					<strong>1. What is SwiftMed?</strong>
					<br /> SwiftMed is a healthcare administration platform
					designed to streamline processes and improve efficiency.
					<br />
					<br />
					<strong>2. How does SwiftMed ensure data security?</strong>
					<br /> We implement strong security measures, including
					encryption, access controls, and compliance with healthcare
					privacy laws.
					<br />
					<br />
					<strong>
						3. Can I integrate SwiftMed with other healthcare
						systems?
					</strong>
					<br /> Yes, SwiftMed supports integration with various
					electronic health record (EHR) systems and third-party
					healthcare solutions.
					<br />
					<br />
					<strong>4. Is there customer support available?</strong>
					<br /> Yes, we offer 24/7 customer support to assist you
					with any issues or questions.
					<br />
					<br />
					<strong>5. How do I get started with SwiftMed?</strong>
					<br /> You can sign up on our website and schedule a demo to
					explore our platform’s capabilities.
				</Typography>
			</Box>
		</Box>
	</Box>
);

export const PrivacyPolicy = () => (
	<Box
		id="privacy-policy"
		sx={{
			padding: 4,
			minHeight: "100vh",
			textAlign: "center",
			paddingTop: "100px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		}}
	>
		<Box sx={{ maxWidth: "600px" }}>
			<Typography variant="h4" gutterBottom>
				Privacy Policy
			</Typography>
			<Box sx={{ textAlign: "left" }}>
				<Typography>
					<strong>1. Introduction</strong>
					<br /> Overview of the Privacy Policy's purpose.
					<br /> Brief statement about the company's commitment to
					protecting user privacy.
					<br />
					<br />
					<strong>2. Information We Collect</strong>
					<br /> Types of personal information collected.
					<br /> Methods of data collection (e.g., directly from
					users, through cookies).
					<br />
					<br />
					<strong>3. How We Use Your Information</strong>
					<br /> Purposes for which the collected information is used
					(e.g., service delivery, customer support).
					<br />
					<br />
					<strong>4. Sharing Your Information</strong>
					<br /> Details on who the information is shared with (e.g.,
					third-party service providers).
					<br /> Circumstances under which information may be shared.
					<br />
					<br />
					<strong>5. Data Security</strong>
					<br /> Measures taken to protect user data.
					<br /> Security practices and protocols.
					<br />
					<br />
					<strong>6. User Rights and Choices</strong>
					<br /> Rights users have regarding their personal
					information (e.g., access, correction, deletion).
					<br /> Choices available to users about data collection and
					usage.
					<br />
					<br />
					<strong>7. Cookies and Tracking Technologies</strong>
					<br /> Explanation of cookies and how they are used.
					<br /> Information on how users can manage cookie
					preferences.
					<br />
					<br />
					<strong>8. Third-Party Links</strong>
					<br /> Policy on links to third-party websites.
					<br /> Disclaimer regarding the privacy practices of
					third-party sites.
					<br />
					<br />
					<strong>9. Data Retention</strong>
					<br /> Duration for which personal information is retained.
					<br /> Criteria used to determine retention periods.
					<br />
					<br />
					<strong>10. International Data Transfers</strong>
					<br /> Information on data transfers to other countries, if
					applicable.
					<br /> Measures to ensure data protection during transfers.
					<br />
					<br />
					<strong>11. Changes to This Privacy Policy</strong>
					<br /> Process for updating the Privacy Policy.
					<br /> How users will be notified of changes.
					<br />
					<br />
					<strong>12. Contact Information</strong>
					<br /> How users can contact the company with questions or
					concerns about the Privacy Policy.
				</Typography>
			</Box>
		</Box>
	</Box>
);

export const TermsOfService = () => (
	<Box
		id="terms-of-service"
		sx={{
			padding: 4,
			minHeight: "100vh",
			textAlign: "center",
			paddingTop: "100px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		}}
	>
		<Box sx={{ maxWidth: "600px" }}>
			<Typography variant="h4" gutterBottom>
				Terms of Service
			</Typography>
			<Box sx={{ textAlign: "left" }}>
				<Typography variant="body1" sx={{ mt: 2 }}>
					Welcome to SwiftMed. These Terms of Service govern your use
					of our platform. By using our services, you agree to comply
					with and be bound by these terms.
				</Typography>
				<Typography variant="h6" sx={{ mt: 4 }}>
					1. Acceptance of Terms
				</Typography>
				<Typography variant="body1">
					By accessing and using SwiftMed, you agree to be bound by
					these terms and all applicable laws.
				</Typography>
				<Typography variant="h6" sx={{ mt: 4 }}>
					2. User Responsibilities
				</Typography>
				<Typography variant="body1">
					You agree to use our services only for lawful purposes and
					in accordance with these terms.
				</Typography>
				<Typography variant="h6" sx={{ mt: 4 }}>
					3. Privacy Policy
				</Typography>
				<Typography variant="body1">
					Your use of SwiftMed is subject to our Privacy Policy, which
					governs how we handle your data.
				</Typography>
				<Typography variant="h6" sx={{ mt: 4 }}>
					4. Contact Information
				</Typography>
				<Typography variant="body1">
					If you have any questions, please contact us at
					support@swiftmed.com.
				</Typography>
			</Box>
		</Box>
	</Box>
);

export const SignUp = ({
	navigateTo,
}: {
	navigateTo: (page: string) => void;
}) => {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		termsAccepted: false,
	});

	const handleChange = (e: any) => {
		const { name, value, type, checked } = e.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleSubmit = (e: any) => {
		e.preventDefault();
		if (!formData.termsAccepted) {
			alert("You must agree to the Terms of Service before registering.");
			return;
		}
		console.log("Form Data:", formData);
		// Handle registration logic here
	};

	return (
		<Container
			maxWidth="sm"
			sx={{ minHeight: "100vh", paddingTop: "100px" }}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					paddingTop: 6,
				}}
			>
				<Typography variant="h4" fontWeight="bold">
					Register
				</Typography>
				<Typography variant="body2" color="gray" sx={{ mt: 1, mb: 3 }}>
					Fill in the form to get started.
				</Typography>

				<form onSubmit={handleSubmit} style={{ width: "100%" }}>
					<Box sx={{ display: "flex", gap: 2, mb: 2 }}>
						<TextField
							fullWidth
							label="First Name"
							name="firstName"
							variant="outlined"
							value={formData.firstName}
							onChange={handleChange}
						/>
						<TextField
							fullWidth
							label="Last Name"
							name="lastName"
							variant="outlined"
							value={formData.lastName}
							onChange={handleChange}
						/>
					</Box>
					<TextField
						fullWidth
						label="Email Address"
						name="email"
						type="email"
						variant="outlined"
						value={formData.email}
						onChange={handleChange}
						sx={{ mb: 2 }}
					/>

					{/* Terms of Service Checkbox */}
					<FormControlLabel
						control={
							<Checkbox
								name="termsAccepted"
								checked={formData.termsAccepted}
								onChange={handleChange}
							/>
						}
						label={
							<Typography variant="body2">
								I agree to the{" "}
								<a
									href="/terms-of-service"
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => navigateTo("sign-up")}
								>
									Terms of Service
								</a>
							</Typography>
						}
						sx={{ mb: 2 }}
					/>

					<Button
						disableElevation
						fullWidth
						type="submit"
						variant="contained"
						sx={{ padding: "12px", backgroundColor: brandColor }}
					>
						Submit
					</Button>
				</form>
			</Box>
		</Container>
	);
};
